import { useQuery } from '@tanstack/react-query';
import { ListProfilesResponse, ProfileResource } from '@ab/ams-school-api/types/private/v1';
import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import { hasKey } from '~/utils';

export type StudentProfile = ProfileResource['attributes'] & { id: string };

const defaultValue: StudentProfile[] = [];

export function useStudentProfiles(
  options?: { enabled?: boolean },
  persona: 'student' | 'agent' | 'admission' = 'student'
) {
  const request = useRequest();
  const { isLoading, isFetching, error, data } = useQuery(
    ['profiles', persona],
    async () => {
      const response = await request<ListProfilesResponse>(
        `${config.apiHost}/private/v1/profiles?${new URLSearchParams({ persona }).toString()}`,
        {
          method: 'GET',
          isExpectedResponse,
        }
      );

      return response.data.map((profile) => ({
        ...profile.attributes,
        id: profile.id,
      }));
    },
    {
      enabled: options?.enabled === undefined ? true : options.enabled,
    }
  );

  return {
    profiles: data ?? defaultValue,
    profilesError: error,
    isFetchingProfiles: isFetching,
    isLoadingProfiles: isLoading,
  };
}

function isExpectedResponse(res: unknown): res is ListProfilesResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res) && Array.isArray(res.data));
}
